"use client";

/**
 * FooterNewsletter — the dark "Newsletter" band that sits at the top of the
 * site footer (replaces the old brand-mark + social-icons row). Adapted from
 * the `cta-newsletter` /design variation, minus the avatar/"7,000+ on the
 * list" row.
 *
 * The submit is wired to the SAME backend as the /beta waitlist form
 * (`POST /api/marketing/beta-signup`, proxied to the marketing FastAPI). For
 * askFinz the newsletter list *is* the private-access waitlist, so an
 * email-only signup posts with placeholder name/country/occupation values and
 * a distinct `source`. The endpoint is idempotent per email.
 */

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FooterNewsletter() {
  const ease = [0.22, 1, 0.36, 1] as const;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      // Same logic/endpoint as the /beta signup form.
      const res = await fetch("/api/marketing/beta-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: "Newsletter",
          lastName: "Subscriber",
          email: clean,
          countryOfResidence: "Unknown",
          occupation: "Subscriber",
          companyName: null,
          intendedUse: null,
          raffleOptIn: false,
          source: "askfinz.com-footer-newsletter",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        detail?: string;
      };
      if (!res.ok) {
        toast.error(data.detail || data.message || "Something went wrong. Please try again.");
        return;
      }
      setDone(true);
      toast.success(data.message || "You’re on the list — we’ll be in touch.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-[#0a0a0a] text-white px-6 py-12 md:px-12 md:py-14">
      {/* banner + ken-burns + gradient */}
      <Image
        src="/shared/banners/wallpaper_banner_05.jpg"
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="ken-burns pointer-events-none absolute inset-0 -z-20 size-full object-cover opacity-40"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(10,10,10,0.65),rgba(10,10,10,0.92))]"
      />

      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center text-center">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="max-w-xl text-3xl font-semibold leading-[1.08] tracking-tight sm:text-4xl"
        >
          Get the changelog — <span className="font-serif italic">what shipped,</span>{" "}
          what&apos;s next.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease }}
          className="mt-4 max-w-md text-base text-white/65"
        >
          Private access is rolling out. Join the list and we&apos;ll bring you in.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.18, ease }}
          className="mt-8 w-full max-w-md"
        >
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease }}
                className="liquid-glass flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white"
              >
                <span className="inline-flex size-5 items-center justify-center rounded-full bg-white text-black">
                  <Check className="size-3.5" />
                </span>
                Thanks — you&apos;re on the list
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleSubmit}
                noValidate
                className="liquid-glass flex items-center gap-1.5 rounded-full p-1.5"
              >
                <label htmlFor="footer-nl-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="footer-nl-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="min-w-0 flex-1 bg-transparent px-4 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Joining…
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
